<script setup lang="ts">
// Bundle Mapbox CSS into the same chunk so it ships only when this
// component is imported (see EclipseMap.vue for the migration rationale).
import 'mapbox-gl/dist/mapbox-gl.css'
import { addEclipsePathLayers } from '~/utils/mapLayers'
import { readCssVar } from '~/utils/theme'

const props = defineProps<{
  lat: number
  lng: number
  sunAzimuth: number | null
  spotName: string
  parkingLat?: number | null
  parkingLng?: number | null
}>()

const config = useRuntimeConfig()
const { t } = useI18n()
const mapContainer = ref<HTMLElement | null>(null)
const mapError = ref('')
const is3D = ref(false)
const showPOIs = ref(true)
let map: any = null
let mapboxgl: any = null
const poiMarkers: any[] = []

// POI categories with icons and colors
const POI_CATEGORIES: Record<string, { icon: string; color: string; label: string }> = {
  parking: { icon: 'P', color: '#3b82f6', label: 'Parking' },
  fuel: { icon: '⛽', color: '#f97316', label: 'Gas station' },
  restaurant: { icon: '🍽', color: '#ef4444', label: 'Restaurant' },
  cafe: { icon: '☕', color: '#a855f7', label: 'Café' },
  toilet: { icon: 'WC', color: '#6b7280', label: 'Toilets' },
  shelter: { icon: '⌂', color: '#22c55e', label: 'Shelter' },
  information: { icon: 'i', color: '#0ea5e9', label: 'Info' },
  hotel: { icon: '🛏', color: '#ec4899', label: 'Accommodation' },
}

// Mapbox POI layer filter expressions for each category
const POI_FILTERS: Record<string, string[]> = {
  parking: ['parking', 'parking_garage'],
  fuel: ['fuel'],
  restaurant: ['restaurant'],
  cafe: ['cafe'],
  toilet: ['toilet'],
  shelter: ['shelter', 'hut'],
  information: ['information', 'visitor_center'],
  hotel: ['hotel', 'hostel', 'guest_house', 'motel', 'camp_site'],
}

function toggle3D() {
  if (!map) return
  is3D.value = !is3D.value
  if (is3D.value) {
    map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
    // Bearing the sun direction only makes sense when we have a real
    // sun_azimuth on the spot row; otherwise just tilt without rotating.
    const bearing = props.sunAzimuth != null ? props.sunAzimuth - 180 : 0
    map.easeTo({ pitch: 60, bearing, duration: 1000 })
  } else {
    map.setTerrain(null)
    map.easeTo({ pitch: 0, bearing: 0, duration: 1000 })
  }
}

function togglePOIs() {
  showPOIs.value = !showPOIs.value
  for (const m of poiMarkers) {
    m.getElement().style.display = showPOIs.value ? '' : 'none'
  }
}

// Approximate metres-between-points using equirectangular projection.
// Good enough for the ~50 m proximity check below; we don't need haversine
// precision in central Iceland.
function approxDistanceM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000
  const x = ((bLng - aLng) * Math.PI / 180) * Math.cos(((aLat + bLat) / 2) * Math.PI / 180)
  const y = (bLat - aLat) * Math.PI / 180
  return Math.hypot(x, y) * R
}

function addCuratedParkingMarker(lat: number, lng: number) {
  if (!map || !mapboxgl) return
  const cat = POI_CATEGORIES.parking!
  const el = document.createElement('div')
  el.className = 'poi-marker poi-marker-curated'
  el.title = t('spot_location.parking_label')
  el.style.cssText = `
    width: 26px; height: 26px; border-radius: 50%;
    background: ${cat.color}; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; line-height: 1;
    border: 2px solid rgb(var(--map-marker-bg));
    box-shadow: 0 0 0 3px ${cat.color}33, 0 1px 6px rgba(0,0,0,0.6);
    cursor: pointer; z-index: 6;
    font-family: system-ui, sans-serif;
  `
  el.textContent = cat.icon

  const ink = `rgb(var(--ink-1))`
  const inkDim = `rgb(var(--ink-1) / 0.62)`
  const popup = new mapboxgl.Popup({
    offset: 14,
    closeButton: false,
    maxWidth: '200px',
    className: 'eclipse-popup',
  }).setHTML(`
    <div style="font-family: 'IBM Plex Mono', monospace; font-size: 12px; color: ${ink}; padding: 4px;">
      <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px;">
        <span style="width: 16px; height: 16px; border-radius: 50%; background: ${cat.color}; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #fff; flex-shrink: 0;">${cat.icon}</span>
        <span style="font-family: 'Manrope', sans-serif; font-weight: 600; font-size: 13px;">${t('spot_location.parking_label')}</span>
      </div>
      <p style="color: ${inkDim}; margin: 0; font-size: 11px;">${t('spot_location.parking_recommended_for', { spot: props.spotName })}</p>
    </div>
  `)

  const marker = new mapboxgl.Marker({ element: el })
    .setLngLat([lng, lat])
    .setPopup(popup)
    .addTo(map)
  poiMarkers.push(marker)
}

function queryAndAddPOIs() {
  if (!map || !mapboxgl) return

  // Query POIs from the composite vector source (works even if POI layers are hidden in dark-v11)
  const features = map.querySourceFeatures('composite', {
    sourceLayer: 'poi_label',
  })

  if (!features.length) return

  // Deduplicate by name+coordinates
  const seen = new Set<string>()
  const uniqueFeatures: any[] = []
  for (const f of features) {
    const coords = f.geometry.coordinates
    const key = `${f.properties.name || ''}|${coords[0].toFixed(4)},${coords[1].toFixed(4)}`
    if (seen.has(key)) continue
    seen.add(key)
    uniqueFeatures.push(f)
  }

  for (const feature of uniqueFeatures) {
    const maki = feature.properties.maki || feature.properties.type || ''
    const name = feature.properties.name || feature.properties.name_en || ''

    // Match to our categories
    let category: string | null = null
    for (const [cat, types] of Object.entries(POI_FILTERS)) {
      if (types.some(t => maki.includes(t) || name.toLowerCase().includes(t))) {
        category = cat
        break
      }
    }
    if (!category) continue

    const cat = POI_CATEGORIES[category]!
    const coords = feature.geometry.coordinates

    // If a curated parking lot is set, suppress Mapbox-tagged parking
    // within ~60 m so we don't render two pins for the same lot.
    if (
      category === 'parking'
      && props.parkingLat != null
      && props.parkingLng != null
      && approxDistanceM(props.parkingLat, props.parkingLng, coords[1], coords[0]) < 60
    ) {
      continue
    }

    const el = document.createElement('div')
    el.className = 'poi-marker'
    el.title = name || cat.label
    // Marker border uses theme-aware --map-marker-bg so it doesn't
     // disappear into the cream tiles in light mode.
    el.style.cssText = `
      width: 24px; height: 24px; border-radius: 50%;
      background: ${cat.color}; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 11px; font-weight: 700; line-height: 1;
      border: 2px solid rgb(var(--map-marker-bg));
      box-shadow: 0 1px 4px rgba(0,0,0,0.5);
      cursor: pointer; z-index: 5;
      font-family: system-ui, sans-serif;
    `
    el.textContent = cat.icon

    const ink = `rgb(var(--ink-1))`
    const inkDim = `rgb(var(--ink-1) / 0.62)`
    const popup = new mapboxgl.Popup({
      offset: 14,
      closeButton: false,
      maxWidth: '200px',
      className: 'eclipse-popup',
    }).setHTML(`
      <div style="font-family: 'IBM Plex Mono', monospace; font-size: 12px; color: ${ink}; padding: 4px;">
        <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px;">
          <span style="width: 16px; height: 16px; border-radius: 50%; background: ${cat.color}; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #fff; flex-shrink: 0;">${cat.icon}</span>
          <span style="font-family: 'Manrope', sans-serif; font-weight: 600; font-size: 13px;">${name || cat.label}</span>
        </div>
        <p style="color: ${inkDim}; margin: 0; font-size: 11px;">${cat.label}</p>
      </div>
    `)

    const marker = new mapboxgl.Marker({ element: el })
      .setLngLat(coords)
      .setPopup(popup)
      .addTo(map)
    poiMarkers.push(marker)
  }
}

watch(mapContainer, async (el) => {
  if (!el || map) return

  const token = config.public.mapboxToken as string
  if (!token) {
    mapError.value = t('map_error.no_token')
    return
  }

  try {
    mapboxgl = (await import('mapbox-gl')).default
    mapboxgl.accessToken = token

    map = new mapboxgl.Map({
      container: el,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [props.lng, props.lat],
      zoom: 14,
      pitch: is3D.value ? 60 : 0,
      bearing: is3D.value && props.sunAzimuth != null ? props.sunAzimuth - 180 : 0,
      attributionControl: false,
      antialias: true,
      // Prevent the map from capturing page scroll/touch:
      //   desktop → requires Ctrl/Cmd + wheel to zoom (shows a hint)
      //   mobile  → requires two-finger pan (one-finger scrolls the page)
      cooperativeGestures: true,
    })

    map.addControl(new mapboxgl.NavigationControl({ showCompass: true, visualizePitch: true }), 'top-right')

    map.on('load', () => {
      if (!map) return

      // 3D terrain
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14,
      })
      if (is3D.value) {
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
      }

      // Sky atmosphere
      map.addLayer({
        id: 'sky',
        type: 'sky',
        paint: {
          'sky-type': 'atmosphere',
          'sky-atmosphere-sun': [0.0, 24.0],
          'sky-atmosphere-sun-intensity': 5,
        },
      })

      // Eclipse totality path + centerline (matches the legend on /map).
      addEclipsePathLayers(map, {
        colors: {
          accent:       readCssVar('--accent', '#f59e0b'),
          accentStrong: readCssVar('--accent-strong', '#fbbf24'),
        },
      })

      // Sun direction wedge — only render when we actually have a real
      // sun_azimuth on the spot row. Without it we'd be silently drawing
      // a default trajectory, which would mislead users planning a shot.
      if (props.sunAzimuth != null) {
        const wedgeGeoJSON = createDirectionWedge(props.lat, props.lng, props.sunAzimuth, 2000, 20)
        map.addSource('sun-direction', { type: 'geojson', data: wedgeGeoJSON })
        map.addLayer({
          id: 'sun-direction-fill',
          type: 'fill',
          source: 'sun-direction',
          paint: {
            'fill-color': '#f59e0b',
            'fill-opacity': 0.08,
          },
        })
        map.addLayer({
          id: 'sun-direction-line',
          type: 'line',
          source: 'sun-direction',
          paint: {
            'line-color': '#f59e0b',
            'line-width': 1.5,
            'line-opacity': 0.4,
            'line-dasharray': [4, 3],
          },
        })

        // Sun direction label at the tip of the wedge
        const tipPoint = movePoint(props.lat, props.lng, props.sunAzimuth, 2000)
        map.addSource('sun-label', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [tipPoint[1], tipPoint[0]] },
            properties: { label: `Sun ☀ ${Math.round(props.sunAzimuth)}°` },
          },
        })
        map.addLayer({
          id: 'sun-label-text',
          type: 'symbol',
          source: 'sun-label',
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 11,
            'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
            'text-offset': [0, -0.8],
            'text-anchor': 'bottom',
          },
          paint: {
            'text-color': '#f59e0b',
            'text-halo-color': '#0a0e17',
            'text-halo-width': 1.5,
          },
        })
      } // end if (props.sunAzimuth != null)

      // Spot marker
      const markerEl = document.createElement('div')
      markerEl.className = 'spot-map-marker'
      new mapboxgl.Marker({ element: markerEl, anchor: 'center' })
        .setLngLat([props.lng, props.lat])
        .addTo(map!)

      // Curated parking marker (overrides Mapbox POI tagging when set on
      // viewing_spots.trailhead_lat/lng). Pushed into poiMarkers so the
      // POI toggle hides/shows it alongside the Mapbox-derived markers.
      if (props.parkingLat != null && props.parkingLng != null) {
        addCuratedParkingMarker(props.parkingLat, props.parkingLng)
      }

      // Query POIs after tiles render
      map.once('idle', () => {
        queryAndAddPOIs()
      })
    })

    map.on('error', (e: any) => {
      console.error('[SpotLocationMap] Mapbox error:', e)
      mapError.value = t('map_error.failed_to_load')
    })
  } catch (err: any) {
    console.error('[SpotLocationMap]', err)
    mapError.value = err.message || t('map_error.failed_to_load')
  }
})

onUnmounted(() => {
  poiMarkers.forEach(m => m.remove())
  poiMarkers.length = 0
  map?.remove()
  map = null
  mapboxgl = null
})

// Move a point along a bearing by a distance in meters
function movePoint(lat: number, lng: number, bearing: number, distanceM: number): [number, number] {
  const R = 6371000
  const d = distanceM / R
  const brng = (bearing * Math.PI) / 180
  const lat1 = (lat * Math.PI) / 180
  const lng1 = (lng * Math.PI) / 180

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng),
  )
  const lng2 = lng1 + Math.atan2(
    Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
  )

  return [(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI]
}

// Create a GeoJSON polygon for a direction wedge
function createDirectionWedge(lat: number, lng: number, azimuth: number, radiusM: number, halfAngle: number) {
  const points: [number, number][] = [[lng, lat]] // center
  const steps = 20
  for (let i = 0; i <= steps; i++) {
    const angle = azimuth - halfAngle + (2 * halfAngle * i) / steps
    const [pLat, pLng] = movePoint(lat, lng, angle, radiusM)
    points.push([pLng, pLat])
  }
  points.push([lng, lat]) // close

  return {
    type: 'Feature' as const,
    geometry: { type: 'Polygon' as const, coordinates: [points] },
    properties: {},
  }
}
</script>

<template>
  <ClientOnly>
    <div v-if="mapError" class="w-full rounded border border-border-subtle/40 bg-surface-raised flex items-center justify-center text-ink-3 font-mono text-sm" style="height: 400px;">
      {{ mapError }}
    </div>
    <div v-else class="relative">
      <div
        ref="mapContainer"
        class="w-full rounded border border-border-subtle/40"
        style="height: 400px;"
      />
      <!-- Map controls overlay (top-left: bottom rows are reserved for the
           Mapbox logo + attribution; top-right has the NavigationControl). -->
      <div class="absolute top-3 left-3 flex gap-1.5 z-10">
        <button
          class="px-2.5 py-1.5 rounded text-[10px] font-mono uppercase tracking-wider transition-all"
          :class="is3D
            ? 'bg-accent/20 border border-accent/40 text-accent'
            : 'bg-surface-raised/80 border border-border-subtle/60 text-ink-3 hover:text-ink-1'"
          @click="toggle3D"
        >
          {{ is3D ? '3D' : '2D' }}
        </button>
        <button
          class="px-2.5 py-1.5 rounded text-[10px] font-mono uppercase tracking-wider transition-all"
          :class="showPOIs
            ? 'bg-accent/20 border border-accent/40 text-accent'
            : 'bg-surface-raised/80 border border-border-subtle/60 text-ink-3 hover:text-ink-1'"
          @click="togglePOIs"
        >
          POI
        </button>
      </div>
    </div>
    <template #fallback>
      <div class="w-full rounded border border-border-subtle/40 bg-surface-raised flex items-center justify-center text-ink-3 font-mono text-sm" style="height: 400px;">
        Loading map…
      </div>
    </template>
  </ClientOnly>
</template>

<style scoped>
:deep(.spot-map-marker) {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #f59e0b;
  border: 2px solid #0a0e17;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.3), 0 2px 10px rgba(0, 0, 0, 0.6);
}

:deep(.poi-marker) {
  transition: transform 0.15s ease;
}

:deep(.poi-marker:hover) {
  transform: scale(1.2);
}

:deep(.mapboxgl-ctrl-compass) {
  background: rgba(10, 16, 32, 0.8) !important;
  border-color: rgba(26, 37, 64, 0.6) !important;
}
</style>
